import { Link } from "react-router-dom";
import {
  CircleNotchIcon as Spinner,
  GraphIcon as Graph,
  WarningCircleIcon as WarningCircle,
} from "@phosphor-icons/react";
import { normalizeComparableObject } from "@/lib/compareSelection";
import { CompareSelectButton } from "@/components/compare/CompareSelectButton";

function fmtBytes(b) {
  if (b == null) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

/** Drill-down card showing every workspace+library where a chosen
 *  file name lives, plus per-occurrence risk markers and a Trace link
 *  into the impact graph for that engagement. */
export default function OccurrenceDetail({ fileName, data, loading, error, compareSelection }) {
  if (!fileName) {
    return (
      <div className="ml-card p-5" data-testid="occurrence-empty-state">
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          Drill-down
        </div>
        <div className="text-sm text-[var(--ml-text-secondary)] mt-2">
          Click a row in the recurring-names table to see every engagement that
          touches that file.
        </div>
      </div>
    );
  }
  return (
    <div className="ml-card p-5" data-testid="occurrence-detail">
      <div className="flex items-baseline justify-between gap-3 mb-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Drill-down
          </div>
          <h2 className="text-lg font-semibold font-mono">{fileName}</h2>
        </div>
        {data && (
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="occurrence-count">
            {data.count} occurrence{data.count === 1 ? "" : "s"}
          </div>
        )}
      </div>

      {loading && (
        <div
          className="flex items-center gap-2 text-sm text-[var(--ml-text-secondary)] py-4"
          data-testid="occurrence-loading"
        >
          <Spinner size={14} className="animate-spin" /> Pulling cross-workspace
          details…
        </div>
      )}

      {error && (
        <div
          className="flex items-center gap-2 text-sm text-[var(--ml-warning)] py-4"
          data-testid="occurrence-error"
        >
          <WarningCircle size={14} weight="bold" />
          {error}
        </div>
      )}

      {data && data.rows.length > 0 && (
        <table className="w-full text-sm" data-testid="occurrence-table">
          <thead>
            <tr className="border-b border-[var(--ml-border)] bg-[var(--ml-surface)]">
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Workspace</th>
              <th className="px-2 py-1.5 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Library</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Records</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Size</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Pgm refs</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Jrn</th>
              <th className="px-2 py-1.5 text-right ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr
                key={`${r.workspace_id}-${r.library_name}`}
                data-testid={`occurrence-row-${r.workspace_id}`}
                className="border-b border-[var(--ml-border)] last:border-b-0"
              >
                <td className="px-2 py-2">
                  <Link
                    to={`/workspaces/${r.workspace_id}`}
                    className="font-semibold hover:text-[var(--ml-primary)] hover:underline"
                  >
                    {r.workspace_name}
                  </Link>
                  {r.client_name && r.client_name !== "—" && (
                    <div
                      className="text-[10px] text-[var(--ml-text-secondary)] mt-0.5"
                      aria-label={`Client: ${r.client_name}`}
                    >
                      <span className="sr-only">Client: </span>
                      {r.client_name}
                    </div>
                  )}
                </td>
                <td className="px-2 py-2 font-mono text-xs">{r.library_name}</td>
                <td className="px-2 py-2 text-right tabular-nums">
                  {r.record_count != null ? r.record_count.toLocaleString() : "—"}
                </td>
                <td className="px-2 py-2 text-right tabular-nums">{fmtBytes(r.file_size)}</td>
                <td className="px-2 py-2 text-right tabular-nums">{r.program_ref_count}</td>
                <td className="px-2 py-2 text-right">
                  {r.journaled ? (
                    <span className="ml-mono text-[10px] uppercase tracking-widest text-[#0e6027]">
                      yes
                    </span>
                  ) : (
                    <span className="ml-mono text-[10px] uppercase tracking-widest text-[#a2191f]">
                      no
                    </span>
                  )}
                </td>
                <td className="px-2 py-2 text-right">
                  <div className="inline-flex flex-wrap items-center justify-end gap-1">
                    <CompareSelectButton
                      obj={normalizeComparableObject(r, { entity: "occurrence" })}
                      selection={compareSelection}
                      compact
                    />
                    <Link
                      to={`/workspaces/${r.workspace_id}/metadata/impact?object=${encodeURIComponent(r.file_name)}&library=${encodeURIComponent(r.library_name)}&from=findings`}
                      data-testid={`occurrence-trace-${r.workspace_id}`}
                      className="inline-flex items-center gap-1 px-1.5 py-0.5 ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] hover:bg-[var(--ml-surface)] border border-transparent hover:border-[var(--ml-border)] transition-colors"
                      title="Open the impact graph for this engagement"
                    >
                      <Graph size={11} weight="bold" />
                      Trace
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
